import { useState, useEffect, useCallback, useMemo } from 'react';
import { Activity, AlertTriangle, Clock, Users, Zap, RefreshCw, TrendingUp, Server } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5" data-testid={testId}>
      <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center mb-3`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <p className="text-2xl font-bold text-white font-space">{value}</p>
      <p className="text-white/40 text-sm">{label}</p>
      {sub && <p className="text-white/25 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function TrafficChart({ data }) {
  const maxReqs = useMemo(() => Math.max(...data.map(d => d.requests), 1), [data]);
  return (
    <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
      <h3 className="text-white font-medium mb-4 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-cyan-400" /> Hourly Traffic (24h)
      </h3>
      <div className="flex items-end gap-0.5 h-32">
        {data.map((d) => {
          const pct = (d.requests / maxReqs) * 100;
          const hasErrors = d.errors > 0;
          return (
            <div key={d.hour} className="flex-1 flex flex-col items-center justify-end group relative h-full">
              <div className="absolute bottom-full mb-1 hidden group-hover:block bg-[#1a2644] text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10 border border-white/10">
                {d.hour?.slice(11)}:00 — {d.requests} reqs, {d.errors} errs
              </div>
              <div
                className={`w-full rounded-t ${hasErrors ? 'bg-gradient-to-t from-red-500/60 to-cyan-500/40' : 'bg-gradient-to-t from-cyan-500/30 to-cyan-500/60'}`}
                style={{ height: `${Math.max(pct, 2)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-white/20 text-[10px]">24h ago</span>
        <span className="text-white/20 text-[10px]">Now</span>
      </div>
    </div>
  );
}

function EndpointTable({ endpoints }) {
  return (
    <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
      <h3 className="text-white font-medium mb-4 flex items-center gap-2">
        <Server className="w-4 h-4 text-purple-400" /> Top Endpoints (24h)
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="endpoint-table">
          <thead>
            <tr className="text-white/30 text-xs border-b border-white/5">
              <th className="text-left py-2 pr-4">Endpoint</th>
              <th className="text-right py-2 px-2">Requests</th>
              <th className="text-right py-2 px-2">Avg (ms)</th>
              <th className="text-right py-2 px-2">Max (ms)</th>
              <th className="text-right py-2 pl-2">Errors</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((ep) => (
              <tr key={ep.endpoint} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                <td className="py-2 pr-4 text-white/70 font-mono text-xs truncate max-w-[200px]">{ep.endpoint}</td>
                <td className="py-2 px-2 text-right text-white/60">{ep.count}</td>
                <td className="py-2 px-2 text-right">
                  <span className={ep.avg_latency_ms > 500 ? 'text-red-400' : ep.avg_latency_ms > 200 ? 'text-amber-400' : 'text-emerald-400'}>
                    {ep.avg_latency_ms}
                  </span>
                </td>
                <td className="py-2 px-2 text-right text-white/40">{ep.max_latency_ms}</td>
                <td className="py-2 pl-2 text-right">
                  <span className={ep.error_count > 0 ? 'text-red-400' : 'text-white/30'}>{ep.error_count}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminHealthDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/health-dashboard`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (err) { logger.error("Operation failed:", err); }
    finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Activity className="w-8 h-8 text-cyan-400 animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-white/40 text-center py-12">Unable to load health dashboard</p>;
  }

  const { latency_24h, latency_1h, errors_24h, errors_1h, top_endpoints, sessions, hourly_traffic } = data;

  return (
    <div className="space-y-8" data-testid="admin-health-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-space text-2xl text-white mb-1">Code Health Dashboard</h1>
          <p className="text-white/40 text-sm">Real-time API performance, error rates, and session metrics</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
          data-testid="refresh-health"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Key metrics row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Zap} label="Latency (p50)" value={`${latency_1h.p50}ms`} sub={`24h: ${latency_24h.p50}ms`} color="bg-cyan-500/20" testId="stat-latency-p50" />
        <StatCard icon={Clock} label="Latency (p95)" value={`${latency_1h.p95}ms`} sub={`24h: ${latency_24h.p95}ms`} color="bg-purple-500/20" testId="stat-latency-p95" />
        <StatCard icon={AlertTriangle} label="Error Rate (1h)" value={`${errors_1h.error_rate}%`} sub={`24h: ${errors_24h.error_rate}%`} color={errors_1h.error_rate > 5 ? 'bg-red-500/20' : 'bg-emerald-500/20'} testId="stat-error-rate" />
        <StatCard icon={Users} label="Active Users (1h)" value={sessions.active_1h} sub={`24h: ${sessions.active_24h} / ${sessions.total_users} total`} color="bg-amber-500/20" testId="stat-active-users" />
      </div>

      {/* Error detail cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <p className="text-white/40 text-xs mb-1">Total Requests (24h)</p>
          <p className="text-3xl font-bold text-white font-space" data-testid="total-requests-24h">{errors_24h.total_requests.toLocaleString()}</p>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <p className="text-white/40 text-xs mb-1">4xx Errors (24h)</p>
          <p className="text-3xl font-bold text-amber-400 font-space" data-testid="errors-4xx">{errors_24h.error_4xx}</p>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <p className="text-white/40 text-xs mb-1">5xx Errors (24h)</p>
          <p className="text-3xl font-bold text-red-400 font-space" data-testid="errors-5xx">{errors_24h.error_5xx}</p>
        </div>
      </div>

      {/* Hourly traffic chart */}
      {hourly_traffic.length > 0 && <TrafficChart data={hourly_traffic} />}

      {/* Endpoint performance table */}
      {top_endpoints.length > 0 && <EndpointTable endpoints={top_endpoints} />}

      {/* Generated timestamp */}
      <p className="text-white/20 text-xs text-right">Generated at {new Date(data.generated_at).toLocaleString()}</p>
    </div>
  );
}
